import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import * as Sentry from '@sentry/react';
import { flushSync } from 'react-dom';

import { streamExerciseChat } from 'api/rest/exercises/exercisesChat';
import { ReactComponent as CloseIcon } from 'assets/icons/general/close.svg';
import { ReactComponent as MaximizeIcon } from 'assets/icons/general/maximize.svg';
import { ReactComponent as ChatHelpIcon } from 'assets/icons/general/question-mark.svg';
import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import { useFeatureFlag } from 'hooks/useFeatureFlags/useFeatureFlags';

function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const useStyles = makeStyles(theme => ({
  messages: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(4),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(1),
    paddingRight: theme.spacing(7),
  },
  messagesWithContent: {
    paddingTop: theme.spacing(12),
  },
  emptyMessage: {
    marginLeft: -theme.spacing(0.75),
    marginTop: -theme.spacing(0.25),
  },
  bubble: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    maxWidth: '100%',
    wordBreak: 'break-word',
  },
  bubbleUser: {
    marginLeft: theme.spacing(6),
    backgroundColor: theme.palette.grey[200],
  },
  bubbleAssistant: {
    marginRight: theme.spacing(6),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  composer: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
  },
  error: {
    padding: theme.spacing(0, 2, 1),
    color: theme.palette.error.main,
    fontSize: theme.typography.caption.fontSize,
  },
  inlineRoot: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 1,
    minHeight: 280,
    maxHeight: 'min(480px, 55vh)',
    overflow: 'hidden',
  },
  chatFrame: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  /** Pinned to top-right of the chat panel; overlaps scrollable messages (position: absolute). */
  toolbar: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.background.paper, 0.82),
    opacity: 0.94,
    boxShadow: theme.shadows[1],
    pointerEvents: 'auto',
    backdropFilter: 'saturate(160%) blur(8px)',
  },
  toolbarIconButton: {
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    '&&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
    },
  },
  toolbarIcon: {
    width: 20,
    height: 20,
    display: 'block',
    '& path': {
      fill: theme.palette.text.secondary,
    },
  },
  toolbarIconStroke: {
    width: 20,
    height: 20,
    display: 'block',
    '& path': {
      fill: theme.palette.text.secondary,
    },
  },
  chatBody: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  inlineHeader: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  inlineBody: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  /** Slightly larger than one-line input for easier tap; bottom-aligned with field. */
  sendIconButton: {
    flexShrink: 0,
    alignSelf: 'flex-end',
    boxSizing: 'border-box',
    height: 56,
    width: 48,
    padding: 0,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.primary.main,
    '&&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.dark,
    },
  },
  fsModalInner: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
    overflowX: 'auto',
    outline: 'none',
    backgroundColor: theme.palette.background.default,
  },
  fsRoot: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    minWidth: 'calc(400px + 6px + 400px)',
    height: '100%',
    overflow: 'hidden',
  },
  fsLeft: {
    minHeight: 0,
    minWidth: 400,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(2, 3, 3),
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
  },
  fsSplitter: {
    flex: '0 0 6px',
    width: 6,
    minWidth: 6,
    padding: 0,
    margin: 0,
    border: 'none',
    borderRadius: 0,
    display: 'block',
    cursor: 'col-resize',
    alignSelf: 'stretch',
    backgroundColor: theme.palette.divider,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -1,
    },
  },
  fsChatPaper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 400,
    height: '100%',
    maxHeight: '100%',
    borderRadius: 0,
    border: 'none',
    overflow: 'hidden',
    boxShadow: 'none',
  },
}));

export type ChatMessage = { id: string; role: 'user' | 'assistant'; content: string };

export type ExerciseChatProps = {
  exerciseId: string | null | undefined;
  disabled?: boolean;
  /** Renders on the left in fullscreen (e.g. exercise toolbar + image). */
  fullscreenExercisePanel?: React.ReactNode;
};

type ChatToolbarMode = 'inline' | 'fullscreen';

const FS_SPLITTER_PX = 6;
const FS_PANE_MIN_PX = 400;
const SPLIT_FALLBACK_MIN = 0.15;
const SPLIT_FALLBACK_MAX = 0.85;

function getInnerSplitWidth(containerWidth: number) {
  return Math.max(1, containerWidth - FS_SPLITTER_PX);
}

function getSplitFractionBounds(containerWidth: number): { min: number; max: number } {
  const inner = getInnerSplitWidth(containerWidth);
  const minF = FS_PANE_MIN_PX / inner;
  const maxF = 1 - FS_PANE_MIN_PX / inner;
  if (maxF < minF) {
    return { min: SPLIT_FALLBACK_MIN, max: SPLIT_FALLBACK_MAX };
  }
  return { min: minF, max: maxF };
}

function clampSplitFraction(value: number, containerWidth: number) {
  const { min, max } = getSplitFractionBounds(containerWidth);
  return Math.min(max, Math.max(min, value));
}

const ExerciseChat: React.FC<ExerciseChatProps> = ({ exerciseId, disabled = false, fullscreenExercisePanel }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('md'));
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [splitFraction, setSplitFraction] = useState(0.5);
  const [splitDragging, setSplitDragging] = useState(false);
  const splitDragRef = useRef<{ startX: number; startSplit: number } | null>(null);
  const fsRootRef = useRef<HTMLDivElement | null>(null);
  const isTester = useFeatureFlag(FEATURE_FLAGS.TESTER);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (fullscreenOpen && !fullscreenExercisePanel) {
      setFullscreenOpen(false);
    }
  }, [fullscreenExercisePanel, fullscreenOpen]);

  useEffect(() => {
    if (fullscreenOpen && !isDesktopLayout) {
      setFullscreenOpen(false);
    }
  }, [fullscreenOpen, isDesktopLayout]);

  useEffect(() => {
    if (!fullscreenOpen) {
      setSplitDragging(false);
      splitDragRef.current = null;
      return undefined;
    }
    const clampCurrentSplit = () => {
      const el = fsRootRef.current;
      if (!el) return;
      const w = el.getBoundingClientRect().width;
      setSplitFraction(s => clampSplitFraction(s, w));
    };
    const rafId = window.requestAnimationFrame(() => {
      const el = fsRootRef.current;
      if (!el) return;
      const w = el.getBoundingClientRect().width;
      setSplitFraction(clampSplitFraction(0.5, w));
    });
    window.addEventListener('resize', clampCurrentSplit);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', clampCurrentSplit);
    };
  }, [fullscreenOpen]);

  useEffect(() => {
    if (!splitDragging) return undefined;
    const onMove = (e: MouseEvent) => {
      const root = fsRootRef.current;
      const drag = splitDragRef.current;
      if (!root || !drag) return;
      const w = root.getBoundingClientRect().width || 1;
      const next = drag.startSplit + (e.clientX - drag.startX) / w;
      setSplitFraction(clampSplitFraction(next, w));
    };
    const onUp = () => {
      setSplitDragging(false);
      splitDragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [splitDragging]);

  const onSplitterMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    splitDragRef.current = { startX: e.clientX, startSplit: splitFraction };
    setSplitDragging(true);
  };

  useEffect(() => {
    setMessages([]);
    setInput('');
    setError(null);
    abortRef.current?.abort();
    abortRef.current = null;
  }, [exerciseId]);

  const appendAssistantDelta = useCallback((delta: string) => {
    if (!delta) return;
    flushSync(() => {
      setMessages(prev => {
        if (prev.length === 0) return [{ id: newMessageId(), role: 'assistant', content: delta }];
        const next = [...prev];
        const last = next[next.length - 1];
        if (last.role === 'assistant') {
          next[next.length - 1] = { ...last, content: last.content + delta };
        } else {
          next.push({ id: newMessageId(), role: 'assistant', content: delta });
        }
        return next;
      });
    });
  }, []);

  const removeTrailingAssistant = useCallback(() => {
    flushSync(() => {
      setMessages(prev => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        if (last.role === 'assistant') {
          return prev.slice(0, -1);
        }
        return prev;
      });
    });
  }, []);

  const send = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || !exerciseId || disabled || isStreaming) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setError(null);
    setInput('');
    const userId = newMessageId();
    const assistantId = newMessageId();
    setMessages(prev => [...prev, { id: userId, role: 'user', content: trimmed }, { id: assistantId, role: 'assistant', content: '' }]);
    setIsStreaming(true);

    try {
      await streamExerciseChat(exerciseId, trimmed, {
        signal: controller.signal,
        onDelta: appendAssistantDelta,
        onStreamError: message => {
          flushSync(() => {
            setError(message);
          });
          removeTrailingAssistant();
        },
      });
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        return;
      }
      const err = e instanceof Error ? e : new Error(String(e));
      const debugInfo = {
        exerciseId,
        errorName: err.name,
        errorMessage: err.message,
        stack: err.stack,
        raw: e,
      };
      if (isTester || process.env.NODE_ENV === 'development') {
        /* eslint-disable no-console -- FEATURE_FLAGS.TESTER: surface stream errors in the console */
        console.error('[ExerciseChat] streamExerciseChat failed');
        console.error(debugInfo);
        /* eslint-enable no-console */
      } else {
        Sentry.captureException(err, {
          tags: { source: 'exercise_chat_stream' },
          extra: { exerciseId },
        });
      }
      const message = err.message || 'Wystąpił błąd, spróbuj ponownie później.';
      setError(message);
      setMessages(prev => {
        if (prev.length >= 2) {
          const last = prev[prev.length - 1];
          if (last.role === 'assistant' && last.content === '') {
            return prev.slice(0, -2);
          }
        }
        return prev;
      });
    } finally {
      setIsStreaming(false);
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, [appendAssistantDelta, disabled, exerciseId, input, isStreaming, isTester, removeTrailingAssistant]);

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      send().catch(() => undefined);
    }
  };

  const handleSendClick = () => {
    send().catch(() => undefined);
  };

  const canSend = Boolean(exerciseId && !disabled && !isStreaming && input.trim());
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;

  if (!exerciseId) {
    return null;
  }

  const chatHelpTooltip = 'Zadawaj pytania wyłącznie o to zadanie. Odpowiedzi mogą zawierać błędy — zawsze zweryfikuj je merytorycznie.';

  const canUseFullscreen = Boolean(fullscreenExercisePanel) && isDesktopLayout;
  const expandTooltip = isDesktopLayout ? 'Pełny widok' : 'Pełny widok jest dostępny tylko na większym ekranie (desktop)';

  const renderToolbar = (mode: ChatToolbarMode) => (
    <Box className={classes.toolbar}>
      <Tooltip enterTouchDelay={300} placement='left' title={chatHelpTooltip}>
        <IconButton aria-label='Informacje o czacie' className={classes.toolbarIconButton} size='small'>
          <ChatHelpIcon className={classes.toolbarIcon} />
        </IconButton>
      </Tooltip>
      {mode === 'inline' ? (
        <Tooltip enterTouchDelay={300} title={expandTooltip}>
          <span>
            <IconButton
              aria-label='Pełny widok'
              className={classes.toolbarIconButton}
              disabled={!canUseFullscreen}
              onClick={() => setFullscreenOpen(true)}
              size='small'
            >
              <MaximizeIcon className={classes.toolbarIconStroke} />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip enterTouchDelay={300} title='Zamknij pełny widok'>
          <IconButton
            aria-label='Zamknij pełny widok'
            className={classes.toolbarIconButton}
            onClick={() => setFullscreenOpen(false)}
            size='small'
          >
            <CloseIcon className={classes.toolbarIconStroke} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const hasMessages = messages.length > 0;

  const renderChatColumn = (mode: ChatToolbarMode) => (
    <Box className={classes.chatFrame}>
      <Box className={classes.chatBody}>
        {renderToolbar(mode)}
        <Box className={`${classes.messages} ${hasMessages ? classes.messagesWithContent : ''}`}>
          {!hasMessages && (
            <Typography color='textSecondary' variant='body1' className={classes.emptyMessage}>
              W czym mogę Ci pomóc?
            </Typography>
          )}
          {messages.map(m => (
            <Box
              key={m.id}
              className={`${classes.bubble} ${m.role === 'user' ? classes.bubbleUser : classes.bubbleAssistant}`}
              component='div'
            >
              <Typography component='div' style={{ whiteSpace: 'pre-wrap' }}>
                {m.content}
                {m.role === 'assistant' && isStreaming && m.id === lastMessageId ? (
                  <Box
                    alignItems='center'
                    component='span'
                    display='inline-flex'
                    marginLeft={m.content ? 0.75 : 0}
                    marginTop={m.content ? 0 : 0.5}
                    style={{ verticalAlign: 'middle' }}
                  >
                    <CircularProgress size={16} />
                  </Box>
                ) : null}
              </Typography>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>
        {error && (
          <Typography className={classes.error} variant='caption'>
            {error}
          </Typography>
        )}
        <Box className={classes.composer}>
          <TextField
            disabled={disabled || isStreaming}
            fullWidth
            multiline
            onChange={e => setInput(e.target.value)}
            minRows={1}
            maxRows={4}
            onKeyDown={onKeyDown}
            placeholder='Zadaj pytanie dotyczące tego zadania...'
            value={input}
            variant='outlined'
          />
          <IconButton aria-label='Wyślij' className={classes.sendIconButton} color='inherit' disabled={!canSend} onClick={handleSendClick}>
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {!fullscreenOpen && (
        <Paper className={classes.inlineRoot} elevation={0} variant='outlined'>
          {renderChatColumn('inline')}
        </Paper>
      )}
      {/* Always mounted: `open` drives visibility so MUI scroll-lock is released on close. */}
      <Modal
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            setFullscreenOpen(false);
          }
        }}
        open={fullscreenOpen}
      >
        {fullscreenOpen ? (
          <div className={classes.fsModalInner} role='presentation' tabIndex={-1}>
            <div ref={fsRootRef} className={classes.fsRoot}>
              <div
                className={classes.fsLeft}
                style={{
                  flex: `${splitFraction} 1 0`,
                }}
              >
                {fullscreenExercisePanel}
              </div>
              <button
                aria-label='Zmień szerokość panelu zadania i czatu'
                className={classes.fsSplitter}
                onMouseDown={onSplitterMouseDown}
                type='button'
              />
              <Paper
                className={classes.fsChatPaper}
                elevation={0}
                style={{
                  flex: `${1 - splitFraction} 1 0`,
                }}
                variant='outlined'
              >
                {renderChatColumn('fullscreen')}
              </Paper>
            </div>
          </div>
        ) : (
          <Box aria-hidden component='span' style={{ display: 'none' }} />
        )}
      </Modal>
    </>
  );
};

export default ExerciseChat;
